import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import socialService from '@services/socialService';
import type { Network, SocialAccount } from '@/types/social';
import { pickAccountId } from '@/utils/social';

const STORAGE_KEY = 'social.account';

const readStored = (): number | null => {
  try {
    return Number(window.localStorage.getItem(STORAGE_KEY)) || null;
  } catch {
    return null;
  }
};

interface Options {
  /** Só contas desta rede (Grade). */
  network?: Network;
  /** Sempre um perfil (Grade): sem escolha válida, o primeiro ativo da rede. */
  required?: boolean;
}

/**
 * Perfil escolhido nas telas das Redes sociais (Grade, Calendário, Posts, Aprovações): vem de
 * `?account=ID` e fica guardado no navegador, para a troca de perfil valer em todas as telas.
 * `null` = todos os perfis. `ready` = contas carregadas (evita buscar duas vezes).
 */
export const useSelectedSocialAccount = ({ network, required }: Options = {}) => {
  const [params, setParams] = useSearchParams();
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [ready, setReady] = useState(false);
  const [stored, setStored] = useState<number | null>(readStored);

  useEffect(() => {
    socialService
      .listAccounts()
      .then(setAccounts)
      .catch(() => undefined)
      .finally(() => setReady(true));
  }, []);

  const requested = Number(params.get('account')) || null;
  const accountId = useMemo(() => {
    const picked = pickAccountId(accounts, requested, stored, network);
    if (picked || !required) return picked;
    return accounts.find((a) => a.is_active && (!network || a.network === network))?.id ?? null;
  }, [accounts, requested, stored, network, required]);

  const setAccountId = useCallback(
    (id: number | null) => {
      try {
        window.localStorage.setItem(STORAGE_KEY, id ? String(id) : 'all');
      } catch {
        // navegador sem armazenamento: vale só pela URL
      }
      setStored(id);
      if (id) params.set('account', String(id));
      else params.delete('account');
      setParams(params, { replace: true });
    },
    [params, setParams],
  );

  const account = accounts.find((a) => a.id === accountId);
  return { accounts, account, accountId, setAccountId, ready };
};
